import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { ArticleConstants } from '../../adapters/helpers/Constants';
import { AssetProps } from '../../types';

const FAQPageSEO = dynamic(() => import('../SEOschema/FAQPageSEO'));
const MarkdownText = dynamic(() => import('../MarkdownText/MarkdownText'));

export interface Topic {
    fields: {
        faqs: FAQ[];
    };
}

export interface FAQ {
    fields: {
        question: string;
        answer: string;
    };
}

interface ArticleFAQProps {
    faqContent?: {
        fields?: {
            searchTitle?: string;
            topics?: Topic[];
            anchorId?: string;
            openFaqIcon?: {
                fields?: {
                    asset?: AssetProps;
                    alternateText?: string;
                };
            };
            closeFaqIcon?: {
                fields?: {
                    asset?: AssetProps;
                    alternateText?: string;
                };
            };
        };
    };
    activeIndex: number;
    topicIndex: number;
}

const ArticleFAQ: React.FC<ArticleFAQProps> = ({ faqContent, activeIndex, topicIndex}) => {
    const { searchTitle, topics, openFaqIcon, closeFaqIcon } = faqContent?.fields || {};
    const topicsQA = topics?.[activeIndex];
    const [activeExpanded, setActiveExpanded] = useState<number>(-1);

    const handleFAQQALink = (index: number) => {
        setActiveExpanded(activeExpanded === index ? -1 : index);
    };

    useEffect(() => {
        setActiveExpanded(-1);
    }, [topicIndex]);

    return (
        <div className='w-full px-2h md:px-0 pb-4 md:pb-1 text-left' id={faqContent?.fields?.anchorId}>
            {searchTitle && (
                <h2 className="text-2xl leading-34 font-semi-bold text-revamp_blueText pb-1 border-b border-solid border-black">
                    {searchTitle}
                </h2>
            )}
            <div className={'w-full pt-1'} id={`ob-faq-qa-index-${activeIndex}`}>
                <ul>
                    {topicsQA?.fields?.faqs?.map((item, index) => (
                        <li key={index} className="py-1" id={`ob-faq-qa-topic-index-${activeIndex}-${index}`}>
                            <div className="flex items-start">
                                <div
                                    className="w-8 pt-[4px] md:pt-[5px]"
                                    id={`active-a-${activeExpanded}-${index}`}
                                >
                                    {activeExpanded === index ? (
                                        <img
                                            src={`https:${closeFaqIcon?.fields?.asset?.fields?.file?.url}`}
                                            alt={closeFaqIcon?.fields?.alternateText || ArticleConstants?.OpenFAQIcon}
                                            width={14}
                                            height={14}
                                            className='w-14p h-14p cursor-pointer'
                                            onClick={() => handleFAQQALink(index)} 
                                        />
                                    ) : (
                                        <img
                                            src={`https:${openFaqIcon?.fields?.asset?.fields?.file?.url}`}
                                            alt={openFaqIcon?.fields?.alternateText || ArticleConstants?.CloseFAQIcon}
                                            width={14}
                                            height={14}
                                            className='w-14p h-14p cursor-pointer'
                                            onClick={() => handleFAQQALink(index)} 
                                        />
                                    )}
                                </div>
                                <h2
                                    className="w-full font-semi-bold text-15 md:text-lg text-revamp_blueText pl-1h cursor-pointer event_button_click"
                                    onClick={() => handleFAQQALink(index)}
                                    data-action-detail={ArticleConstants?.ExpandFAQ}
                                >
                                    {item?.fields?.question}
                                </h2>
                            </div>
                            <div className='font-regular text-base py-1 pl-3h' style={{ display: activeExpanded === index ? 'block' : 'none' }}>
                                <MarkdownText className="answer" value={item?.fields?.answer} />
                            </div>
                        </li>
                    ))}
                </ul>
                {topicsQA?.fields?.faqs && (
                    <FAQPageSEO
                        faqs={topicsQA?.fields?.faqs}
                        topics={topics}
                        isCustomSearch
                        questions={topicsQA?.fields?.faqs?.map(faq => faq?.fields?.question)}
                        answers={topicsQA?.fields?.faqs?.map(faq => faq?.fields?.answer)}
                    />
                )}
            </div>
        </div>
    );
};

export default ArticleFAQ;